/* =====================================================================================
   The Ariadne Suite registry.

   Forum is one app among several, and the whole premise of this project was not having a
   hundred disconnected systems. So every suite app is reachable from the shell and from
   the command palette.

   TWO ACCESS TIERS, and the distinction is deliberate:

     'sso'      Thread, Library, Sculptor, Compass. Opens straight in if the member's
                permissions allow it. Ordinary working tools.

     'step_up'  Notebook and Locker. Requires a FRESH SECOND FACTOR before opening: a
                passkey touch or a TOTP code, valid for 15 minutes. Not a password retype.
                It is a real cryptographic re-authentication that takes about a second, and
                the database enforces it too (see db/locker_notebook_rls.sql), so it is not
                merely a screen you can navigate around.

   THE TIER HERE IS ONLY THE FALLBACK. Which apps actually demand the step-up is tenant
   data: app.suite_access (migration 0027), read by SuiteLauncher at open. A white-label
   tenant for whom MFA is optional turns it off in their own database with one UPDATE and
   no deployment; nothing tenant-specific is hardcoded here. The registry tier applies only
   while the rows load, in demo builds with no backend, and for an app with no row, and it
   falls back strict.

   URLs are taken from the verified subdomain map, which contains one trap worth knowing:
   thread.meetariadne.app is a MARKETING SITE. The actual Thread application lives at
   app.meetariadne.app. Getting that wrong sends a clinician to a brochure.
   ===================================================================================== */

export type AccessTier = 'sso' | 'step_up';

export type StepUpMethod = 'passkey' | 'totp';

export interface SuiteApp {
  key: string;
  name: string;
  tagline: string;
  url: string;
  tier: AccessTier;
  icon: string;
  /** Where its data actually lives, which is what determines the security posture. */
  backend: string;
  /** Shown in the step-up dialog so the reason for the friction is explicit. */
  sensitivity?: string;
  /** Preferred second factor. Passkey where the device supports it, TOTP otherwise. */
  stepUpMethod?: StepUpMethod;
  note?: string;
  status: 'live' | 'not_deployed';
}

export const SUITE: SuiteApp[] = [
  {
    key: 'thread', name: 'Ariadne Thread',
    tagline: 'Clinical record and consultation workspace',
    url: 'https://app.meetariadne.app',
    tier: 'sso', icon: 'thread', status: 'live',
    backend: 'Node/Express API on api.thread.meetariadne.app, AWS RDS Postgres',
    note:
      'Note the URL. thread.meetariadne.app is the marketing site; the application is at ' +
      'app.meetariadne.app. Thread is the one suite app not on Supabase, so it will not ' +
      'share a session with the others without work.',
  },
  {
    key: 'library', name: 'Ariadne Library',
    tagline: 'Reference corpus and publication tracking',
    url: 'https://library.meetariadne.app',
    tier: 'sso', icon: 'library', status: 'live',
    backend: 'Supabase slulkwjhrbhciinjdaph',
    note: 'Feeds the claims library in Studio: an approved claim cites a Library reference.',
  },
  {
    key: 'sculptor', name: 'Ariadne Sculptor',
    tagline: 'Form design and structured data capture',
    url: 'https://sculptor.meetariadne.app',
    tier: 'sso', icon: 'sculptor', status: 'live',
    backend: 'Supabase brcouzikfhppdirbhjwa (shared with Locker)',
    note:
      'Sculptor holds form templates only. A COMPLETED form is written into Locker: its code ' +
      'writes locker_records, locker_record_versions, locker_subjects, locker_sites and ' +
      'locker_audit. It shares a Supabase project with Locker, which has a security ' +
      'consequence set out in docs/SUITE_ACCESS.md.',
  },
  {
    key: 'compass', name: 'Ariadne Compass',
    tagline: 'Guidance and decision support',
    url: 'https://compass.meetariadne.app',
    tier: 'sso', icon: 'compass', status: 'live',
    backend: 'Supabase fmwbrfzcvrclkuxqxfne',
    note: 'meet.meetariadne.app is an orphaned stale duplicate. Do not link to it.',
  },
  {
    key: 'notebook', name: 'Ariadne Notebook',
    tagline: 'Electronic laboratory notebook',
    url: 'https://notebook.meetariadne.app',
    tier: 'step_up', icon: 'notebook', status: 'live', stepUpMethod: 'totp',
    backend: 'Supabase yspbfdqugbsgdsinbmtu',
    sensitivity:
      'Mission-critical experimental data. Entries are evidence: they support patent priority, ' +
      'regulatory submissions and analytical validation. An unauthorised edit is not recoverable ' +
      'by re-running the experiment.',
  },
  {
    key: 'locker', name: 'Ariadne Locker',
    tagline: 'Records repository and retention',
    url: 'https://locker.meetariadne.app',
    tier: 'step_up', icon: 'locker', status: 'live', stepUpMethod: 'totp',
    backend: 'Supabase brcouzikfhppdirbhjwa (shared with Sculptor)',
    sensitivity:
      'Where patient data is filed, including completed consent forms. Bulk access to this ' +
      'record set is the highest-consequence action available in the suite, and re-authentication ' +
      'is required every time regardless of how recently you signed in elsewhere.',
  },
];

/** Vault appears in suite branding but has no deployed subdomain, so it is not listed. */
export const NOT_DEPLOYED = ['Ariadne Vault'];

export const TIER_LABEL: Record<AccessTier, string> = {
  sso: 'One click, no sign-in',
  step_up: 'A six-digit code from your authenticator',
};

/* How long a step-up stays valid. Matches locker_stepup_fresh() in the database, so the
   interface and the data layer expire together rather than drifting apart. */
export const STEP_UP_WINDOW_MIN = 15;
